import { useEffect, useState } from "react";
import { useStorytellerStore } from "@/stores/storytellerStore";
import { connectFirebase } from "./session";
import type { RoomBackend } from "./backend";
import { classifyStorytellerError, lifecycleMessage } from "./lifecycle";
import { applyTravelerChoice, commitTravelerChoiceLocally, observeTravelerChoice, pendingTravelerChoiceParticipation } from "./membershipCommands";
import { endGameWithIntent } from "./terminal";
import { closeMultiplayerSession, dismissUnattachedCleanupFailure, initialConnectionStatus, leaveMultiplayerOffline, reportRuntimeError, retryStorytellerSession, scopeKey, useSessionRuntime, useStorytellerSync } from "./storytellerSync";

export function StorytellerSession() {
  const lobby = useStorytellerStore(s => s.lobby);
  const view = useStorytellerStore(s => s.view);
  const retry = useSessionRuntime(s => s.retry);
  const [backend, setBackend] = useState<RoomBackend | null>(null);
  useEffect(() => {
    let active = true;
    setBackend(null);
    if (!lobby) {
      useSessionRuntime.setState({ status: "idle", failure: null, closeFailed: false, leaveOffer: null });
      return;
    }
    useSessionRuntime.setState({ status: initialConnectionStatus(lobby) });
    void connectFirebase().then(connection => {
      if (!active) return;
      if (connection.uid !== lobby.uid) throw new Error("Session authorization changed.");
      setBackend(connection.backend);
    }).catch(error => {
      if (!active) return;
      const failure = classifyStorytellerError(error, "startup");
      reportRuntimeError("connect", failure.message);
      useSessionRuntime.setState({ status: "failed", failure });
    });
    return () => { active = false; };
  }, [lobby?.code, retry]);
  useStorytellerSync(backend);
  useApplyTravelerChoices(lobby?.code);
  // The Grimoire renders its own status in-flow beneath its header.
  return view === "game" ? null : <ConnectionStatus />;
}

/**
 * ASTRA-10G-R1-001: the Storyteller-level warning for superseded lobbies whose
 * authoritative cleanup failed. Rendered by the App shell, so it spans Home,
 * New Game and the game / review screen; it is not the current lobby's status
 * (ConnectionStatus) and offers only Dismiss.
 */
export function UnattachedLobbyWarnings() {
  const failures = useSessionRuntime(s => s.unattachedCleanupFailures);
  if (!failures.length) return null;
  return <>
    {failures.map(failure => (
      <div key={failure.code} className="connection-status" data-tone="error" role="alert" data-unattached-lobby={failure.code}>
        <strong>A multiplayer lobby may still be open</strong>
        <span className="connection-status-message">{failure.message}</span>
        <span className="connection-status-actions">
          <button type="button" className="btn btn-sm" onClick={() => dismissUnattachedCleanupFailure(failure.code)}>Dismiss</button>
        </span>
      </div>
    ))}
  </>;
}

const DEV = (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV === true;

/** Storyteller-visible diagnostic: expose only the failure category, Firebase
 * error code, and attributed operation/path. The raw SDK error text stays
 * development-only so production troubleshooting can identify the denied
 * rule without surfacing arbitrary backend error content. */
function sanitizedTechnicalDetails(failure: { category: string; diagnostic: string }): string {
  const parts = failure.diagnostic.split(" | ");
  const code = parts.find(part => part.startsWith("code="));
  const operation = parts.find(part => part.startsWith("operation="));
  const boundedOperation = operation ? operation.slice(0, 600) : undefined;
  return [failure.category, code, boundedOperation].filter(Boolean).join(" | ");
}

/**
 * Compact, in-flow multiplayer connection status for the Storyteller. It
 * never overlays Grimoire controls: callers place it in normal document flow.
 * "Connecting…"/"Reconnecting…" are polite status text with no action; a
 * failure is an alert offering only the action that can actually help:
 * - live writer stopped or runtime error: Reconnect (the existing retry seam);
 * - session ended: Leave lobby (closeMultiplayerSession clears an ended lobby);
 * - startup never reached live: Retry, or End multiplayer (authoritative
 *   close, keeping the local game);
 * - that close also failed: Try ending again, plus -- only when offered for a
 *   lobby the server proved never reached live -- Leave multiplayer, keeping
 *   the game offline (local only; re-proven when clicked).
 * PR-10H-004: while a Finish Game intent is outstanding, every one of those
 * end-the-lobby actions is instead "Retry finishing game", which retries that
 * exact retained intent through the one terminal seam (endGameWithIntent) --
 * never a generic close without the result and its read-back.
 * Messages are plain language. A sanitized category/code/operation diagnostic
 * is available behind Technical details in every build; raw SDK error text is
 * appended only in development builds.
 */
export function ConnectionStatus() {
  const lobby = useStorytellerStore(s => s.lobby);
  const { status, error, errors, failure, closeFailed, leaveOffer } = useSessionRuntime();
  const terminalIntent = useStorytellerStore(s => s.terminalClose?.intent ?? null);
  const [busy, setBusy] = useState(false);
  if (!lobby || status === "idle") return null;
  const run = (action: () => Promise<unknown>) => async () => {
    if (busy) return;
    setBusy(true);
    try { await action(); } catch { /* recorded in the runtime status */ } finally { setBusy(false); }
  };
  const endMultiplayer = terminalIntent ? run(() => endGameWithIntent(terminalIntent)) : run(closeMultiplayerSession);
  const endLabel = (generic: string) => terminalIntent ? "Retry finishing game" : generic;
  if ((status === "connecting" || status === "reconnecting") && !error) {
    return <div className="connection-status" data-tone="info" role="status" aria-live="polite">
      {status === "connecting" ? "Connecting to the lobby…" : "Reconnecting to the lobby…"}
    </div>;
  }
  if (status === "live" && !error && !closeFailed) return null;

  let title = "Multiplayer problem";
  let message = error ?? failure?.message ?? "The lobby connection needs attention.";
  const actions: { label: string; onClick: () => void; danger?: boolean }[] = [];
  if (closeFailed) {
    title = "The lobby could not be ended";
    message = errors.close ?? message;
    actions.push({ label: endLabel("Try ending again"), onClick: endMultiplayer });
    if (leaveOffer === scopeKey(lobby)) actions.push({ label: "Leave multiplayer — keep game offline", onClick: run(leaveMultiplayerOffline), danger: true });
  } else if (failure?.category === "ended" && status !== "live") {
    title = "This lobby has ended";
    actions.push({ label: endLabel("Leave lobby"), onClick: endMultiplayer });
  } else if (status === "failed") {
    title = "Multiplayer is not live";
    actions.push({ label: "Retry", onClick: retryStorytellerSession });
    actions.push({ label: endLabel("End multiplayer"), onClick: endMultiplayer });
  } else if (status === "blocked") {
    title = "Reconnect needs attention";
    actions.push({ label: "Retry", onClick: retryStorytellerSession });
  } else if (status === "stopped") {
    title = "Multiplayer interrupted";
    actions.push({ label: "Reconnect", onClick: retryStorytellerSession });
  } else if (status === "live") {
    actions.push({ label: "Reconnect", onClick: retryStorytellerSession });
  } else {
    title = status === "connecting" ? "Connecting to the lobby…" : "Reconnecting to the lobby…";
  }
  const technicalDetails = failure ? sanitizedTechnicalDetails(failure) : null;
  return <div className="connection-status" data-tone="error" role="alert">
    <strong>{title}</strong>
    <span className="connection-status-message">{message}</span>
    {actions.length > 0 && <span className="connection-status-actions">
      {actions.map(action => <button key={action.label} type="button" className={`btn btn-sm${action.danger ? " btn-danger" : ""}`} disabled={busy} onClick={action.onClick}>{action.label}</button>)}
    </span>}
    {technicalDetails && <details className="connection-status-diagnostic">
      <summary>Technical details</summary>
      <code>{DEV && failure?.diagnostic ? `${technicalDetails} | raw=${failure.diagnostic}` : technicalDetails}</code>
    </details>}
  </div>;
}

/**
 * Player-side Traveler character choice (Phase 9 Setup finalization B4):
 * once selected, the choice is public immediately -- it does not wait for
 * the Storyteller to click anything, matching "player chooses Traveller
 * character" with no separate approval step. This applies each observed
 * choice through the Role seam (resolveRoles) the Storyteller's own manual
 * Traveler override uses (never a second mutation path), and the
 * Storyteller's override remains fully available afterward: applying a
 * choice is just another Role change, not a lock. Phase 10D: each observed
 * request is bound, when observed, to the participation instance holding the
 * seat, and is applied only while the request itself is unchanged and the
 * authoritative roster record still names that same participation
 * (ParticipantId stays Storyteller-private -- the player-written request
 * carries only a character), so a request from an earlier participation can
 * never apply to a replacement. Independent of
 * which screen is visible, matching useStorytellerSync's own scope.
 *
 * Precedence (Phase 9 Setup finalization B4 revision): a current
 * Storyteller-assigned Traveler character always wins over an older
 * pending player choice. If the seat already carries a Traveler character
 * by the time this request is processed -- whether the Storyteller assigned
 * it before or after the player submitted their choice -- that assignment
 * stands; the stale request is simply cleared, never overwritten back to
 * the player's pick. A choice only ever applies to a Traveler who does not
 * yet have a character.
 *
 * Authority (FINAL SETUP INTEGRATION REVISION, Section 1): the request node
 * lives under the same Storyteller-authoritative collection as every other
 * membership write, and the Firebase rules require the current fenced
 * writer's guard to clear it -- writing through StorytellerSession's own raw
 * connection (available as soon as it connects, before writer authority is
 * ever claimed) can locally apply a character while failing with
 * permission_denied clearing the request. This reads the same
 * useSessionRuntime().backend every other membership command
 * (seatPlayerAndCommit, revokePlayerAndCommit, ...) already uses -- never a
 * second write path -- and simply does not process anything until that
 * fenced writer is actually available; a request left pending while
 * disconnected/reconnecting is retried automatically once it appears, from
 * this same effect re-running on its next value.
 *
 * Phase 10D (CLOSURE-02): likewise, a request observed while its seat holds
 * no participation yet (e.g. the Storyteller's own seating acknowledgment has
 * not landed) is retried automatically when that seat's participation
 * changes: `participation` is a focused derivation of exactly the
 * participations the pending requests would be observed for, so the
 * effect re-runs then -- no polling, no reaction to unrelated game changes --
 * and each retry is observed for, and revalidated against, the participation
 * that is current at that moment.
 */
export function useApplyTravelerChoices(code: string | undefined) {
  const travelerChoices = useSessionRuntime(s => s.travelerChoices);
  const writer = useSessionRuntime(s => s.backend);
  const participation = useStorytellerStore(s => pendingTravelerChoiceParticipation(s.game, travelerChoices));
  useEffect(() => {
    if (!writer || !code) return;
    for (const [uid, { playerId, roleId }] of Object.entries(travelerChoices)) {
      if (!playerId) continue; // unresolved for now; retried once the roster resolves it
      // Phase 10D (ASTRA-10D-001): bound HERE, when the request is observed, to
      // the participation holding the seat. The queued callback revalidates the
      // request and that participation instead of re-resolving whoever holds
      // the seat by the time it runs.
      const observed = observeTravelerChoice(playerId, roleId);
      if (!observed) continue; // no participation holds the seat right now
      applyTravelerChoice(writer, code, uid, observed, commitTravelerChoiceLocally)
        .catch(error => reportRuntimeError("traveler-choice", lifecycleMessage(error)));
    }
    // `participation` is read through observeTravelerChoice above; it is a
    // dependency so that a change of it re-runs this effect (CLOSURE-02).
  }, [writer, code, travelerChoices, participation]);
}
